/**
 * Single source of truth for API paths. Add the constant here first, then
 * the TanStack Query hook, then the MSW handler, then the
 * docs/api-contract.md entry (same workflow as TCM).
 */
export const API_ENDPOINTS = {
  editor: {
    examples: (id: number) =>
      `/api/secretmissions/editor/missions/${id}/examples/`,
    example: (id: number, slot: number) =>
      `/api/secretmissions/editor/missions/${id}/examples/${slot}/`,
    doorImage: (id: number) =>
      `/api/secretmissions/editor/missions/${id}/door-image/`,
    session: "/api/secretmissions/editor/session/",
    phoneCode: "/api/secretmissions/editor/phone-code/",
    missions: "/api/secretmissions/editor/missions/",
    mission: (id: number) => `/api/secretmissions/editor/missions/${id}/`,
  },
  webchat: {
    session: "/api/secretmissions/webchat/session/",
    chats: "/api/secretmissions/webchat/chats/",
    contacts: "/api/secretmissions/webchat/contacts/",
    assistants: "/api/secretmissions/webchat/assistants/",
    groups: "/api/secretmissions/webchat/groups/",
    ticket: "/api/secretmissions/webchat/ws-ticket/",
    history: (id: string) => `/api/secretmissions/webchat/chats/${id}/history/`,
    sync: (id: string) => `/api/secretmissions/webchat/chats/${id}/sync/`,
    messages: (id: string) =>
      `/api/secretmissions/webchat/chats/${id}/messages/`,
    messageAction: (id: string, root: string, action: string) =>
      `/api/secretmissions/webchat/chats/${id}/messages/${root}/${action}/`,
    search: (id: string) => `/api/secretmissions/webchat/chats/${id}/search/`,
    preferences: (id: string) =>
      `/api/secretmissions/webchat/chats/${id}/preferences/`,
    attachments: (id: string) =>
      `/api/secretmissions/webchat/chats/${id}/attachments/`,
    attachment: (id: string, attachId: string) =>
      `/api/secretmissions/webchat/chats/${id}/attachments/${attachId}/`,
    receipts: (id: string) =>
      `/api/secretmissions/webchat/chats/${id}/receipts/`,
    handoff: (id: string) => `/api/secretmissions/webchat/chats/${id}/handoff/`,
  },
  companion: {
    open: "/api/secretmissions/companion/",
    profile: "/api/secretmissions/companion/profile/",
    mission: (id: string | number) =>
      `/api/secretmissions/companion/mission/${id}/`,
    history: (id: string) => `/api/secretmissions/webchat/chats/${id}/history/`,
    messages: (id: string) =>
      `/api/secretmissions/webchat/chats/${id}/messages/`,
  },
  stepper: {
    /**
     * Where the visitor is in the web half of an adventure, and every WEB step
     * still ahead of them, each with its options and branches. Server-derived
     * from their journey rows, so it survives a cleared cache or a new device.
     */
    position: (id: string | number) => `/api/secretmissions/position/${id}/`,
    /** Log one ai.journey row for a step the visitor just entered. */
    journey: (id: string | number) => `/api/secretmissions/journey/${id}/`,
  },
  funnel: {
    /** Key-bound session: referrer name, handler shortcode, brand strings. */
    session: "/api/v1/funnel/session",
    /** Handler roster for the chooser cards. */
    handlers: "/api/v1/funnel/handlers",
  },
  session: {
    /** On a new visit, mint a temporary member + session (no phone yet). */
    ensure: "/api/secretmissions/session/",
  },
  mission: {
    /** Stepper-owned adaptive debrief: start or send a reviewed answer. */
    debrief: "/api/secretmissions/debrief/",
    /** POST multipart audio; returns a transcript for the agent to review. */
    transcribe: "/api/secretmissions/transcribe/",
    /** Save the operative's first name, engraved on their key before the
     *  doors; the texts address them by it. */
    name: "/api/secretmissions/name/",
    /** POST {glowId}: they offer a debrief to be featured as a Field Report. */
    featureReview: "/api/secretmissions/feature-review/",
    /** The operative's clearance token (GET) — a link their people use to
     *  view their dossier. */
    dossierLink: "/api/secretmissions/dossier/",
    /** A dossier by clearance token: name, missions completed, locks. */
    dossier: (token: string) => `/api/secretmissions/dossier/${token}/`,
    /** Claim (or re-fetch) the visitor's unique mission codename. */
    codename: "/api/secretmissions/mission-codename/",
    /** One glow.missionbank row — the orders shown in the mission kit. */
    bank: (missionId: string | number) =>
      `/api/secretmissions/missionbank/${missionId}/`,
    /** Today's two doors for this member: label per family, MissionID per
     *  difficulty. Picking a door then a difficulty resolves one MissionID,
     *  which rides through to stepper so the label and the SMS agree. */
    doors: "/api/secretmissions/doors/",
    /** Files staged for one mission, grouped by kind (video/audio/txt/other).
     *  Authored on disk under MEDIA_ROOT/missionkit/<id>/, so the kit picks up
     *  new assets with no deploy. */
    kitMedia: (missionId: string | number) =>
      `/api/secretmissions/missionkit/${missionId}/media/`,
    /** The operative's file: their SecretMission glows (debriefs), newest first. */
    file: "/api/secretmissions/file/",
    /** Book tomorrow's text. The step names the event and delay (its
     *  Schedule* options); the browser only names the step. Needs a phone. */
    nextDay: (adventureId: string | number) =>
      `/api/secretmissions/next-day/${adventureId}/`,
  },
  glow: {
    /** Glow's own post endpoint. Type "SecretMission" skips judging and is
     *  hidden from the Glow feed — it only shows up in the operative's file. */
    createPost: "/api/goodglows/createpost/",
  },
  auth: {
    /**
     * Text a six-digit login code. Secret Missions' own endpoint, not Glow's:
     * the wording, the code lifetime and what verify does are ours to change
     * without breaking a contract Glow depends on.
     */
    getPhoneCode: "/api/secretmissions/phone-code/",
    /**
     * Verify the code and bind the session to the operative. Unlike Glow's, an
     * unknown number claims the temporary member this session already holds
     * rather than minting a second one — so a codename or KEY already earned
     * survives the login. `claimed` says which happened.
     */
    verifyPhoneCode: "/api/secretmissions/verify-phone/",
  },
  qr: {
    /** Get-or-create the member's KEY QR; returns a sharable link + encoded code. */
    key: "/api/secretmissions/key-qr/",
    /** Resolve a KEY code (secretmissions.com/code/<code>) to the referrer's name. */
    referrer: (code: string) => `/api/secretmissions/referrer/${code}/`,
  },
} as const;
